import { useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  Pressable,
  type GestureResponderEvent,
} from "react-native";
import Svg, { Polygon } from "react-native-svg";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from "react-native-reanimated";
import { haptic } from "../haptics";
import { colors, typography } from "../theme/tokens";

export type AnimatedSaveBookmarkProps = {
  bookmarked: boolean;
  onPress: (event: GestureResponderEvent) => void;
  size?: number;
  accessibilityLabel?: string;
  savedLabel?: string;
  saveAccessibilityLabel?: string;
  removeAccessibilityLabel?: string;
  activeColor?: string;
  inactiveIconColor?: string;
  activeBackgroundColor?: string;
};

const INACTIVE_BACKGROUND = "rgba(0, 0, 0, 0.05)";

type PolygonPoint = readonly [number, number];
type PolygonShape = readonly PolygonPoint[];

const BOOKMARK_SHAPE: PolygonShape = [
  [8, 4],
  [24, 4],
  [24, 28],
  [16, 23],
  [8, 28],
];
const BOOKMARK_POINTS = BOOKMARK_SHAPE.map(([x, y]) => `${x},${y}`).join(" ");
const COLLAPSED_TAB: PolygonShape = [
  [16, 14],
  [16, 14],
  [16, 14],
  [16, 14],
];
const TOP_PAPER_TAB: PolygonShape = [
  [8, 5],
  [16, 1.5],
  [24, 5],
  [16, 9],
];
const LEFT_PAPER_TAB: PolygonShape = [
  [3, 8],
  [11, 4.5],
  [11, 25],
  [4, 28],
];
const RIGHT_PAPER_TAB: PolygonShape = [
  [21, 4.5],
  [29, 8],
  [28, 28],
  [21, 25],
];
const TUCK_COLORS = ["#F0B24D", "#E98A91", "#91B7A4"];
const TUCK_RANGE = [0, 0.5, 1] as const;

const AnimatedPolygon = Animated.createAnimatedComponent(Polygon);

function keyframePolygonPoints(
  progress: number,
  inputRange: readonly number[],
  shapes: readonly PolygonShape[],
) {
  "worklet";

  const points: string[] = [];
  const firstShape = shapes[0];
  if (!firstShape) return "";
  const pointCount = firstShape.length;

  for (let index = 0; index < pointCount; index += 1) {
    const x = interpolate(
      progress,
      inputRange,
      shapes.map((shape) => shape[index]?.[0] ?? 0),
      Extrapolation.CLAMP,
    );
    const y = interpolate(
      progress,
      inputRange,
      shapes.map((shape) => shape[index]?.[1] ?? 0),
      Extrapolation.CLAMP,
    );
    points.push(`${x},${y}`);
  }

  return points.join(" ");
}

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReducedMotion);
    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReducedMotion,
    );

    return () => subscription.remove();
  }, []);

  return reducedMotion;
}

function BookmarkScene({
  progress,
  size,
  inactiveIconColor,
  activeColor,
}: {
  progress: SharedValue<number>;
  size: number;
  inactiveIconColor: string;
  activeColor: string;
}) {
  const activeCoreProps = useAnimatedProps(() => ({
    opacity: interpolate(
      progress.value,
      TUCK_RANGE,
      [0, 0.5, 1],
      Extrapolation.CLAMP,
    ),
  }));
  const topPaperProps = useAnimatedProps(() => ({
    points: keyframePolygonPoints(progress.value, TUCK_RANGE, [
      COLLAPSED_TAB,
      TOP_PAPER_TAB,
      COLLAPSED_TAB,
    ]),
  }));
  const leftPaperProps = useAnimatedProps(() => ({
    points: keyframePolygonPoints(progress.value, TUCK_RANGE, [
      COLLAPSED_TAB,
      LEFT_PAPER_TAB,
      COLLAPSED_TAB,
    ]),
  }));
  const rightPaperProps = useAnimatedProps(() => ({
    points: keyframePolygonPoints(progress.value, TUCK_RANGE, [
      COLLAPSED_TAB,
      RIGHT_PAPER_TAB,
      COLLAPSED_TAB,
    ]),
  }));

  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      accessibilityRole="image"
    >
      {/* The bookmark remains the recognizable anchor while paper tabs tuck
          out from behind it and retract into the saved state. */}
      <AnimatedPolygon animatedProps={topPaperProps} fill={TUCK_COLORS[0]} />
      <AnimatedPolygon animatedProps={leftPaperProps} fill={TUCK_COLORS[1]} />
      <AnimatedPolygon animatedProps={rightPaperProps} fill={TUCK_COLORS[2]} />
      <Polygon points={BOOKMARK_POINTS} fill={inactiveIconColor} />
      <AnimatedPolygon
        points={BOOKMARK_POINTS}
        animatedProps={activeCoreProps}
        fill={activeColor}
      />
    </Svg>
  );
}

export function AnimatedSaveBookmark({
  bookmarked,
  onPress,
  size = 32,
  accessibilityLabel,
  savedLabel = "Saved",
  saveAccessibilityLabel = "Save article",
  removeAccessibilityLabel = "Remove article from saved",
  activeColor = colors.warm,
  inactiveIconColor = colors.textLight,
  activeBackgroundColor = `${colors.warm}22`,
}: AnimatedSaveBookmarkProps) {
  const capturePress = usePressCapture("AnimatedSaveBookmark");
  const reducedMotion = useReducedMotion();
  const progress = useSharedValue(bookmarked ? 1 : 0);
  const previousBookmarked = useRef(bookmarked);
  // Keep the vector inside the same visual bounds as the original 18px
  // lucide bookmark; the tucked paper tabs can extend behind it.
  const iconSize = Math.max(16, size - 14);

  useEffect(() => {
    if (previousBookmarked.current === bookmarked) return;
    previousBookmarked.current = bookmarked;

    if (reducedMotion) {
      progress.value = bookmarked ? 1 : 0;
      return;
    }

    progress.value = withTiming(bookmarked ? 1 : 0, {
      duration: 420,
      easing: Easing.linear,
    });
  }, [bookmarked, progress, reducedMotion]);

  const surfaceStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      progress.value,
      [0, 1],
      [INACTIVE_BACKGROUND, activeBackgroundColor],
    ),
    width: interpolate(
      progress.value,
      [0, 1],
      [size, size + 48],
      Extrapolation.CLAMP,
    ),
    transform: [
      {
        scale: interpolate(
          progress.value,
          TUCK_RANGE,
          [1, 1.03, 1],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }));

  const sceneStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 500 },
      {
        translateY: interpolate(
          progress.value,
          TUCK_RANGE,
          [0, -1, 0],
          Extrapolation.CLAMP,
        ),
      },
      {
        rotateZ: `${interpolate(
          progress.value,
          TUCK_RANGE,
          [0, -6, 0],
          Extrapolation.CLAMP,
        )}deg`,
      },
      {
        scale: interpolate(
          progress.value,
          TUCK_RANGE,
          [1, 1.04, 1],
          Extrapolation.CLAMP,
        ),
      },
      {
        scaleX: interpolate(
          progress.value,
          TUCK_RANGE,
          [1, 0.92, 1],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }));

  const iconContainerStyle = useAnimatedStyle(() => ({
    left: interpolate(
      progress.value,
      [0, 1],
      [(size - iconSize) / 2, 8],
      Extrapolation.CLAMP,
    ),
    top: (size - iconSize) / 2,
  }));

  const savedLabelStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      progress.value,
      [0, 0.8, 0.94, 1],
      [0, 0, 0.85, 1],
      Extrapolation.CLAMP,
    ),
    transform: [
      {
        translateX: interpolate(
          progress.value,
          [0, 0.8, 0.94, 1],
          [8, 8, 0, 0],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }));

  const handlePress = (event: GestureResponderEvent) => {
    capturePress(
      pressLabel(
        accessibilityLabel ??
          (bookmarked ? removeAccessibilityLabel : saveAccessibilityLabel),
      ),
      bookmarked ? "remove_bookmark" : "save_bookmark",
    );
    // Saving gets a distinct selection tick; unsaving stays light.
    haptic(bookmarked ? "light" : "selection");
    onPress(event);
  };

  return (
    <Animated.View
      style={[
        {
          height: size,
          borderRadius: size / 2,
          overflow: "visible",
        },
        surfaceStyle,
      ]}
    >
      <Pressable
        onPress={handlePress}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={
          accessibilityLabel ??
          (bookmarked ? removeAccessibilityLabel : saveAccessibilityLabel)
        }
        accessibilityState={{ selected: bookmarked }}
        style={({ pressed }) => ({
          flex: 1,
          position: "relative",
          opacity: pressed ? 0.72 : 1,
          overflow: "visible",
        })}
      >
        <Animated.View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              width: iconSize,
              height: iconSize,
              alignItems: "center",
              justifyContent: "center",
            },
            iconContainerStyle,
          ]}
        >
          <Animated.View
            style={[
              {
                position: "absolute",
                alignItems: "center",
                justifyContent: "center",
              },
              sceneStyle,
            ]}
          >
            <BookmarkScene
              progress={progress}
              size={iconSize}
              inactiveIconColor={inactiveIconColor}
              activeColor={activeColor}
            />
          </Animated.View>
        </Animated.View>
        <Animated.Text
          accessible={false}
          numberOfLines={1}
          style={[
            {
              position: "absolute",
              left: iconSize + 13,
              top: (size - 14) / 2,
              color: activeColor,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.sm,
              lineHeight: 14,
              fontWeight: "500",
              letterSpacing: 0.2,
            },
            savedLabelStyle,
          ]}
        >
          {savedLabel}
        </Animated.Text>
      </Pressable>
    </Animated.View>
  );
}

export default AnimatedSaveBookmark;
